<template>
  <div class="deal-proposals">
    <!-- Header with New Proposal Button -->
    <div class="deal-proposals__header">
      <span class="deal-proposals__count">{{ proposalCountLabel }}</span>
      <SplitBtn
        :text="$t('deals.new_proposal')"
        variant="primary"
        icon="plus"
        :menu-items="formMenuItems"
        @click="createManualProposal"
        @menu-item-click="openForm"
      />
    </div>

    <!-- Loading -->
    <div class="deal-proposals__loading" v-if="loading">
      <div class="deal-proposals__spinner"></div>
    </div>

    <!-- Empty State -->
    <div class="deal-proposals__empty" v-else-if="formattedProposals.length === 0">
      <BaseIcon name="file-text" size="32" />
      <p>{{ $t('deals.no_proposals') }}</p>
      <span class="deal-proposals__empty-hint">
        {{ $t('deals.create_proposal_hint') }}
      </span>
    </div>

    <!-- Proposals Table -->
    <div class="deal-proposals__table" v-else>
      <EasyDataTable
        :headers="headers"
        :items="formattedProposals"
        :loading="loading"
        :rows-per-page="50"
        hide-footer
        @click-row="handleRowClick"
        table-class-name="custom-table"
      >
        <template #item-title="item">
          <TableRowLink
            v-if="isProposalRowLinked(item)"
            :to="proposalRoute(item)"
            :aria-label="`${$t('proposals.view')} ${item.title}`"
          >
            <span class="deal-proposals__title-cell">
              <span>{{ item.title }}</span>
              <ProposalTypeBadge v-if="isMeasurementWorkflow" :proposal-type="item.proposalType" />
            </span>
          </TableRowLink>
          <div v-else class="deal-proposals__title-cell">
            <span>{{ item.title }}</span>
            <ProposalTypeBadge v-if="isMeasurementWorkflow" :proposal-type="item.proposalType" />
          </div>
        </template>
        <template #item-status="item">
          <ProposalStatusBadge :status="item.status" size="small" />
        </template>
        <template #item-actions="item">
          <div class="actions" @click.stop>
            <!-- Draft: edit actions -->
            <SplitBtn
              v-if="item.status === 'draft'"
              variant="tertiary"
              size="sm"
              :menu-items="[
                { label: $t('proposals.edit'), value: 'edit' },
                { label: $t('proposals.duplicate'), value: 'duplicate' },
                { label: $t('proposals.send'), value: 'send' },
                { label: $t('proposals.delete'), value: 'delete', danger: true },
              ]"
              @click="handleAction('edit', item)"
              @menu-item-click="(value) => handleAction(value, item)"
            >
              {{ $t('proposals.edit') }}
            </SplitBtn>
            <!-- Sent/Viewed: view actions -->
            <SplitBtn
              v-else-if="item.status === 'sent' || item.status === 'viewed'"
              variant="tertiary"
              size="sm"
              :menu-items="[
                { label: $t('proposals.view'), value: 'view' },
                { label: $t('proposals.edit_anyway'), value: 'edit_anyway' },
                { label: $t('proposals.resend'), value: 'resend' },
                { label: $t('proposals.duplicate'), value: 'duplicate' },
                { label: $t('proposals.mark_accepted'), value: 'mark_accepted' },
                { label: $t('proposals.mark_rejected'), value: 'mark_rejected', danger: true },
                { label: $t('proposals.withdraw'), value: 'withdraw', danger: true },
              ]"
              @click="handleAction('view', item)"
              @menu-item-click="(value) => handleAction(value, item)"
            >
              {{ $t('proposals.view') }}
            </SplitBtn>
            <!-- Rejected/Withdrawn: view with restore -->
            <SplitBtn
              v-else-if="item.status === 'rejected' || item.status === 'withdrawn'"
              variant="tertiary"
              size="sm"
              :menu-items="[
                { label: $t('proposals.view'), value: 'view' },
                { label: $t('proposals.duplicate'), value: 'duplicate' },
                { label: $t('proposals.restore'), value: 'restore' },
              ]"
              @click="handleAction('view', item)"
              @menu-item-click="(value) => handleAction(value, item)"
            >
              {{ $t('proposals.view') }}
            </SplitBtn>
            <!-- Accepted: view with edit -->
            <SplitBtn
              v-else-if="item.status === 'accepted'"
              variant="tertiary"
              size="sm"
              :menu-items="[
                { label: $t('proposals.view'), value: 'view' },
                { label: $t('proposals.edit_anyway'), value: 'edit_anyway' },
                { label: $t('proposals.duplicate'), value: 'duplicate' },
              ]"
              @click="handleAction('view', item)"
              @menu-item-click="(value) => handleAction(value, item)"
            >
              {{ $t('proposals.view') }}
            </SplitBtn>
            <!-- Other statuses -->
            <SplitBtn
              v-else
              variant="tertiary"
              size="sm"
              :menu-items="[
                { label: $t('proposals.view'), value: 'view' },
                { label: $t('proposals.duplicate'), value: 'duplicate' },
              ]"
              @click="handleAction('view', item)"
              @menu-item-click="(value) => handleAction(value, item)"
            >
              {{ $t('proposals.view') }}
            </SplitBtn>
          </div>
        </template>
      </EasyDataTable>
    </div>

    <!-- Next step for the current proposal situation -->
    <div class="deal-proposals__next-step" v-if="!loading && nextStepHint">
      <BaseIcon name="arrow-right" size="16" />
      <p>{{ nextStepHint }}</p>
    </div>
  </div>
</template>

<script>
  import { ref, computed, onMounted, getCurrentInstance } from 'vue';
  import { useDealStore } from '@/store/dealStore';
  import { useUserStore } from '@/store/userStore';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import { formService } from '@/services/formService';
  import { crmService } from '@/services/crmService';
  import {
    openSalesOrderNotice,
    salesOrderBlockFromError,
    salesOrderRevertPreview,
  } from '@/utils/salesOrderNotice';
  import { formatDate, formatDateShort } from '@/utils/date';
  import { formatProposalNumber } from '@shared/utils/proposalNumbering';
  import { useOpenProposalConfigurator } from '@/composables/useOpenProposalConfigurator';

  import EasyDataTable from 'vue3-easy-data-table';
  import 'vue3-easy-data-table/dist/style.css';

  import BaseIcon from '@shared/components/BaseIcon.vue';
  import SplitBtn from '@/components/partials/SplitBtn.vue';
  import TableRowLink from '@/components/tables/TableRowLink.vue';
  import ProposalStatusBadge from '@/components/partials/ProposalStatusBadge.vue';
  import ProposalTypeBadge from '@/components/partials/ProposalTypeBadge.vue';
  import { PROPOSAL_WORKFLOW, PROPOSAL_TYPES } from '@/constants/proposalTypes';
  import { getRejectReasonModalProps } from '@/utils/proposalRejectReason';

  import { useRouter } from 'vue-router';

  export default {
    name: 'DealProposals',

    components: {
      EasyDataTable,
      BaseIcon,
      SplitBtn,
      TableRowLink,
      ProposalStatusBadge,
      ProposalTypeBadge,
    },

    props: {
      dealId: {
        type: String,
        required: true,
      },
      deal: {
        type: Object,
        default: null,
      },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const dealStore = useDealStore();
      const userStore = useUserStore();
      const uiStore = useUIStore();
      const toastStore = useToastStore();
      const router = useRouter();

      const configuratorForms = ref([]);
      const loadingForms = ref(false);

      const proposals = computed(() => dealStore.dealProposals);
      const loading = computed(() => dealStore.dealProposalsLoading);

      const isMeasurementWorkflow = computed(() => {
        return (
          userStore.currentOrganization?.settings?.proposal_workflow ===
          PROPOSAL_WORKFLOW.MEASUREMENT
        );
      });

      // A deal with an accepted definitive proposal is "closed" sales-wise: new
      // requests belong in a new deal, otherwise the deal value/revenue breaks.
      // Accepted proformas don't block, so the proforma → definitive flow keeps working.
      const hasAcceptedDefinitiveProposal = computed(() => {
        return proposals.value.some(
          (p) =>
            p.status === 'accepted' &&
            (p.proposal_type || PROPOSAL_TYPES.DEFINITIVE) === PROPOSAL_TYPES.DEFINITIVE
        );
      });

      const proposalCountLabel = computed(() => {
        const count = proposals.value.length;
        if (!count) return '';
        return count === 1
          ? $t('deals.proposal_count_one')
          : $t('deals.proposal_count_other', { count });
      });

      // Situational guidance below the table: which move follows from the
      // proposals that are currently on the deal.
      const nextStepHint = computed(() => {
        const active = proposals.value.filter(
          (p) => !['superseded', 'withdrawn'].includes(p.status)
        );
        if (!active.length) return '';

        const ofType = (type) =>
          active.filter((p) => (p.proposal_type || PROPOSAL_TYPES.DEFINITIVE) === type);
        const proformas = ofType(PROPOSAL_TYPES.PROFORMA);
        const definitives = ofType(PROPOSAL_TYPES.DEFINITIVE);
        const has = (list, ...statuses) => list.some((p) => statuses.includes(p.status));
        // Once a proforma exists the two quote types are meaningful, even when
        // the organization doesn't run the measurement workflow by default.
        const twoStep = isMeasurementWorkflow.value || proformas.length > 0;

        let key;
        if (has(definitives, 'accepted')) key = 'accepted';
        else if (has(definitives, 'draft')) key = twoStep ? 'definitive_draft' : 'draft';
        else if (has(definitives, 'sending', 'sent', 'viewed'))
          key = twoStep ? 'definitive_sent' : 'sent';
        else if (has(proformas, 'accepted')) key = 'proforma_accepted';
        else if (has(proformas, 'draft')) key = 'proforma_draft';
        else if (has(proformas, 'sending', 'sent', 'viewed')) key = 'proforma_sent';
        else key = 'rejected';

        return $t(`deals.proposal_hints.${key}`);
      });

      const headers = [
        { text: '', value: 'number', width: 100 },
        { text: $t('proposals.title_field'), value: 'title' },
        { text: $t('proposals.status'), value: 'status', width: 120 },
        { text: $t('proposals.total_amount'), value: 'amount', width: 120 },
        { text: $t('proposals.created_at'), value: 'createdAt', width: 120 },
        { text: '', value: 'actions', width: 120 },
      ];

      // Helper: Get netto value (excl. BTW)
      const getNettoValue = (proposal) => {
        const total = proposal.total_amount || 0;
        if (proposal.prices_include_vat === false) return total;
        if (proposal.subtotal_excl_vat != null) return proposal.subtotal_excl_vat;
        const vatRate = proposal.vat_rate ?? 21;
        return Math.round((total / (1 + vatRate / 100)) * 100) / 100;
      };

      const formattedProposals = computed(() => {
        const organization = userStore.currentOrganization
          ? { settings: userStore.currentOrganization.settings || {} }
          : undefined;

        return proposals.value.map((proposal) => {
          const formattedNumber =
            formatProposalNumber(proposal, organization) || proposal.proposal_number;

          return {
            id: proposal.id,
            createdAt: formatDateShort(proposal.created_at),
            title: proposal.title,
            status: proposal.status,
            proposalType: proposal.proposal_type || 'definitive',
            number: formattedNumber,
            leadId: proposal.lead?.id,
            amount: new Intl.NumberFormat('nl-NL', {
              style: 'currency',
              currency: proposal.currency || 'EUR',
            }).format(proposal.subtotal_excl_vat || getNettoValue(proposal)),
          };
        });
      });

      // Menu items for SplitBtn
      const formMenuItems = computed(() => {
        return configuratorForms.value.map((form) => ({
          label: form.name,
          value: form.id,
        }));
      });

      const fetchForms = async () => {
        loadingForms.value = true;
        try {
          const response = await formService.getForms();
          const forms = response.forms || response || [];
          configuratorForms.value = forms.filter((form) => form.type === 'configurator');
        } catch (error) {
          console.error('Failed to fetch forms:', error);
        } finally {
          loadingForms.value = false;
        }
      };

      const dealLeadId = () => props.deal?.lead?.id || props.deal?.lead_id;

      const openCreateDealModal = async (onDealCreated) => {
        const { default: CreateDealModal } = await import('@/components/deals/CreateDealModal.vue');
        const createModalId = uiStore.addModal(CreateDealModal, {
          title: $t('deals.create_deal'),
          preselectedLeadId: dealLeadId(),
          pipelineId: props.deal?.pipeline_id || null,
          onSubmit: (newDeal) => {
            uiStore.closeModal(createModalId);
            onDealCreated(newDeal);
          },
        });
      };

      // Guard: a deal with an accepted definitive proposal shouldn't get new
      // proposals — offer to create a new deal for the new request instead.
      const promptNewDealForProposal = (onDealCreated) => {
        const modalId = uiStore.addModal(null, {
          title: $t('deals.proposal_locked_title'),
          description: $t('deals.proposal_locked_description'),
          status: 'warning',
          submitText: $t('deals.proposal_locked_create_deal'),
          cancelText: $t('common.cancel'),
          onSubmit: () => {
            uiStore.closeModal(modalId);
            openCreateDealModal(onDealCreated);
          },
        });
      };

      const goToProposalCreate = (dealId, extraQuery = {}) => {
        const query = { ...extraQuery };
        const leadId = dealLeadId();
        if (leadId) query.lead_id = leadId;
        if (dealId) query.deal_id = dealId;
        router.push({ name: 'ProposalCreate', query });
      };

      const createManualProposal = () => {
        if (hasAcceptedDefinitiveProposal.value) {
          promptNewDealForProposal((newDeal) => goToProposalCreate(newDeal.id));
          return;
        }
        goToProposalCreate(props.dealId);
      };

      const { openProposalConfigurator } = useOpenProposalConfigurator();

      const openForm = (formId) => {
        const form = configuratorForms.value.find((f) => f.id === formId);
        if (!form) return;

        const openWithDeal = (dealId) => {
          openProposalConfigurator(
            {
              formId: form.id,
              dealId,
              leadId: dealLeadId() || null,
            },
            $t
          );
        };

        if (hasAcceptedDefinitiveProposal.value) {
          promptNewDealForProposal((newDeal) => openWithDeal(newDeal.id));
          return;
        }
        openWithDeal(props.dealId);
      };

      const handleRowClick = (row) => {
        if (row.status === 'superseded' || row.status === 'withdrawn') return;
        if (row.status === 'draft') {
          editProposal(row);
        } else {
          viewProposal(row);
        }
      };

      const isProposalRowLinked = (proposal) => {
        return proposal.status !== 'superseded' && proposal.status !== 'withdrawn';
      };

      const proposalRoute = (proposal) => ({
        name: 'ProposalDetail',
        params: { id: proposal.id },
      });

      const handleAction = (action, item) => {
        switch (action) {
          case 'edit':
            editProposal(item);
            break;
          case 'edit_anyway':
            editProposalAnyway(item);
            break;
          case 'duplicate':
            duplicateProposal(item);
            break;
          case 'delete':
            deleteProposal(item);
            break;
          case 'withdraw':
            withdrawProposal(item);
            break;
          case 'restore':
            restoreProposal(item);
            break;
          case 'mark_accepted':
            markProposalAsAccepted(item);
            break;
          case 'mark_rejected':
            markProposalAsRejected(item);
            break;
          case 'view':
          case 'send':
          case 'resend':
            viewProposal(item);
            break;
        }
      };

      const editProposal = (proposal) => {
        router.push({ name: 'ProposalDetail', params: { id: proposal.id } });
      };

      const editProposalAnyway = (proposal) => {
        router.push({ name: 'ProposalDetail', params: { id: proposal.id } });
      };

      const viewProposal = (proposal) => {
        router.push({ name: 'ProposalDetail', params: { id: proposal.id } });
      };

      const duplicateProposal = (proposal) => {
        const extraQuery = { duplicate_from: proposal.id };
        if (hasAcceptedDefinitiveProposal.value) {
          promptNewDealForProposal((newDeal) => goToProposalCreate(newDeal.id, extraQuery));
          return;
        }
        goToProposalCreate(props.dealId, extraQuery);
      };

      const deleteProposal = (proposal) => {
        const modalId = uiStore.addModal(null, {
          title: $t('proposals.delete_proposal'),
          description: $t('proposals.delete_proposal_confirmation'),
          status: 'danger',
          onSubmit: async () => {
            try {
              await crmService.deleteProposal(proposal.leadId, proposal.id);
              await dealStore.fetchDealProposals(props.dealId);
              uiStore.closeModal(modalId);
              toastStore.addToast({ message: $t('proposals.proposal_deleted'), type: 'success' });
            } catch (error) {
              console.error('Error deleting proposal:', error);
              toastStore.addToast({
                message: error.response?.data?.error || $t('proposals.error_deleting'),
                type: 'error',
              });
              uiStore.closeModal(modalId);
            }
          },
        });
      };

      const withdrawProposal = (proposal) => {
        const modalId = uiStore.addModal(null, {
          title: $t('proposals.withdraw_proposal'),
          description: $t('proposals.withdraw_proposal_confirmation'),
          status: 'warning',
          showReasonInput: true,
          reasonLabel: $t('proposals.withdraw_reason_label'),
          reasonPlaceholder: $t('proposals.withdraw_reason_placeholder'),
          onSubmit: async (reason) => {
            try {
              await crmService.withdrawProposal(proposal.leadId, proposal.id, reason || null);
              await dealStore.fetchDealProposals(props.dealId);
              uiStore.closeModal(modalId);
              toastStore.addToast({ message: $t('proposals.proposal_withdrawn'), type: 'success' });
            } catch (error) {
              console.error('Error withdrawing proposal:', error);
              toastStore.addToast({
                message: error.response?.data?.error || $t('proposals.error_withdrawing'),
                type: 'error',
              });
              uiStore.closeModal(modalId);
            }
          },
        });
      };

      const restoreProposal = (proposal) => {
        const modalId = uiStore.addModal(null, {
          title: $t('proposals.restore_proposal'),
          description: $t('proposals.restore_proposal_confirmation'),
          status: 'info',
          onSubmit: async () => {
            try {
              await crmService.restoreProposal(proposal.leadId, proposal.id);
              await dealStore.fetchDealProposals(props.dealId);
              uiStore.closeModal(modalId);
              toastStore.addToast({ message: $t('proposals.proposal_restored'), type: 'success' });
            } catch (error) {
              console.error('Error restoring proposal:', error);
              toastStore.addToast({
                message: error.response?.data?.error || $t('proposals.error_restoring'),
                type: 'error',
              });
              uiStore.closeModal(modalId);
            }
          },
        });
      };

      const markProposalAsAccepted = (proposal) => {
        const modalId = uiStore.addModal(null, {
          title: $t('proposals.mark_accepted_title'),
          description: $t('proposals.mark_accepted_confirmation'),
          status: 'success',
          onSubmit: async () => {
            uiStore.updateModalProps(modalId, { loading: true });
            try {
              await crmService.markProposalAsAccepted(proposal.leadId, proposal.id, null);
              await dealStore.fetchDealProposals(props.dealId);
              uiStore.closeModal(modalId);
              toastStore.addToast({
                message: $t('proposals.proposal_marked_accepted'),
                type: 'success',
              });
            } catch (error) {
              console.error('Error marking proposal as accepted:', error);
              uiStore.updateModalProps(modalId, { loading: false });
              toastStore.addToast({
                message: $t('proposals.error_marking_accepted'),
                type: 'error',
              });
            }
          },
        });
      };

      const markProposalAsRejected = async (proposal) => {
        const preview =
          proposal.status === 'accepted'
            ? await salesOrderRevertPreview(proposal.id, 'reject')
            : null;
        if (preview && !preview.allowed) {
          openSalesOrderNotice(uiStore, preview);
          return;
        }

        const modalId = uiStore.addModal(null, {
          title: preview?.title || $t('proposals.mark_rejected_title'),
          description: preview?.description || $t('proposals.mark_rejected_confirmation'),
          status: 'warning',
          ...getRejectReasonModalProps($t),
          onSubmit: async (reason, { reasonCode } = {}) => {
            uiStore.updateModalProps(modalId, { loading: true });
            try {
              await crmService.markProposalAsRejected(
                proposal.leadId,
                proposal.id,
                reason || null,
                reasonCode || null
              );
              await dealStore.fetchDealProposals(props.dealId);
              uiStore.closeModal(modalId);
              toastStore.addToast({
                message: $t('proposals.proposal_marked_rejected'),
                type: 'success',
              });
            } catch (error) {
              console.error('Error marking proposal as rejected:', error);
              const blocked = salesOrderBlockFromError(error);
              uiStore.updateModalProps(modalId, { loading: false });
              if (blocked) {
                uiStore.closeModal(modalId);
                openSalesOrderNotice(uiStore, blocked);
                return;
              }
              toastStore.addToast({
                message: $t('proposals.error_marking_rejected'),
                type: 'error',
              });
            }
          },
        });
      };

      const updateProposalInStore = (data) => {
        if (!data) return;
        dealStore.fetchDealProposals(props.dealId);
      };

      onMounted(async () => {
        await fetchForms();
      });

      return {
        proposals,
        loading,
        headers,
        formattedProposals,
        configuratorForms,
        formMenuItems,
        isMeasurementWorkflow,
        proposalCountLabel,
        nextStepHint,
        createManualProposal,
        openForm,
        handleRowClick,
        isProposalRowLinked,
        proposalRoute,
        handleAction,
      };
    },
  };
</script>

<style scoped lang="scss">
  .deal-proposals {
    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      margin-bottom: 1rem;
    }

    &__count {
      color: var(--slate-600);
      font-size: 13px;
      line-height: 20px;
      @include ellipsis(1);
    }

    &__next-step {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      margin-top: 1rem;
      padding: 0.75rem 1rem;
      border-radius: 4px;
      background: var(--slate-200);
      color: var(--slate-600);

      .icon {
        flex-shrink: 0;
        margin-top: 2px;
      }

      p {
        margin: 0;
        font-size: 13px;
        line-height: 20px;
      }
    }

    &__loading {
      display: flex;
      justify-content: center;
      padding: 3rem;
    }

    &__spinner {
      width: 24px;
      height: 24px;
      border: 2px solid var(--slate-200);
      border-top-color: var(--brand-500);
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    &__empty {
      text-align: center;
      padding: 3rem 2rem;
      background: white;
      border: 1px solid var(--slate-300);
      border-radius: 4px;
      color: var(--slate-400);

      p {
        margin: 0.75rem 0 0 0;
        font-size: 0.9375rem;
        font-weight: 500;
        color: var(--slate-600);
      }
    }

    &__empty-hint {
      display: block;
      margin-top: 0.25rem;
      font-size: 0.8125rem;
      color: var(--slate-400);
    }

    &__table {
      overflow: hidden;
      background: white;
      border: 1px solid var(--slate-300);
      border-radius: 4px;

      :deep(.vue3-easy-data-table) {
        --easy-table-border: none;
        --easy-table-row-border: 1px solid var(--slate-200);
        --easy-table-header-font-size: 10px;
        --easy-table-header-font-color: var(--slate-600);
        --easy-table-header-background-color: white;
        --easy-table-body-row-font-size: 13px;
        --easy-table-body-row-font-color: var(--slate-900);
        --easy-table-body-row-background-color: white;
        --easy-table-body-row-hover-background-color: var(--slate-50);
      }

      :deep(.vue3-easy-data-table__main) {
        border: none;
      }

      :deep(.vue3-easy-data-table__header th) {
        padding: 0.75rem 1rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.025em;
      }

      :deep(.vue3-easy-data-table__body td) {
        padding: 0.875rem 1rem;
      }

      :deep(.vue3-easy-data-table__body tr) {
        cursor: pointer;
      }
    }

    &__title-cell {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .actions {
      display: flex;
      justify-content: flex-end;
    }
  }
</style>
